import { inject, Injectable, NgZone, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';

/** a running app asks the server for a new version this often */
const CHECK_INTERVAL = 15 * 60 * 1000;
/** a tab coming back to the screen checks too, at most this often */
const MIN_CHECK_GAP = 60 * 1000;
const CHUNK_RELOAD_KEY = 'apiz.chunkReloadAt';

/**
 * New frontend versions (bundles and translations) without a forced cache reset.
 *
 * The service worker downloads a new version in the background, but an open app keeps the version it started with
 * until it is reloaded, and it only looked for one at start. Here: a check at start, every 15 minutes and when the
 * tab comes back; once a new version is ready, `updateReady` shows the reload banner (app component). A broken
 * worker cache reloads at once. See docs/frontend/README.md (Frontend updates).
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  readonly #sw = inject(SwUpdate, { optional: true });
  readonly #zone = inject(NgZone);
  readonly updateReady = signal(false);
  #lastCheck = 0;
  #started = false;

  start(): void {
    if (this.#started || !this.#sw?.isEnabled) return;
    this.#started = true;
    this.#sw.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.updateReady.set(true));
    // the cached version cannot be served any more (files gone): only a reload gives a working app
    this.#sw.unrecoverable.subscribe(() => location.reload());
    // timers and the visibility listener stay outside Angular (no change detection for a check)
    this.#zone.runOutsideAngular(() => {
      setInterval(() => this.#check(), CHECK_INTERVAL);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.#check();
      });
    });
    this.#check();
  }

  /** the unsaved changes guard (beforeunload) still asks first */
  reload(): void {
    location.reload();
  }

  dismiss(): void {
    this.updateReady.set(false);
  }

  #check(): void {
    const now = Date.now();
    if (this.updateReady() || now - this.#lastCheck < MIN_CHECK_GAP) return;
    this.#lastCheck = now;
    this.#sw.checkForUpdate().catch(() => {}); // offline or server busy: next time
  }
}

/**
 * A lazy chunk of the previous version is gone after a deployment (no service worker, or before it updated):
 * reload once to get the new version, instead of an error on every screen not loaded yet.
 * @return true when a reload was started
 */
export function reloadOnChunkLoadError(error: any): boolean {
  const message = `${error?.message ?? error ?? ''}`;
  if (!/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk [\w-]+ failed/i.test(message)) {
    return false;
  }
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch {
    return false; // no storage: no way to stop a reload loop
  }
  if (Date.now() - last < 60_000) return false; // reloaded a minute ago: the file is really missing, show the error
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  location.reload();
  return true;
}
